import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Angebotsvorlage, Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, Objektliste, Objektzeile,
         useBeforeUnload } from "@kundeninfo/ui";
import type { VerlassenWaechter } from "../../router.js";
import { Meldung, feldFehlerAus, meldungAus, useVorfilter, vorgabeUnternehmen }
  from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import { PlatzhalterTextfeld } from "../../components/PositionsEditor.js";
import { Zahlplan } from "../../components/Zahlplan.js";
import type { ZahlplanModalClient } from "../../components/Zahlplan.js";
import { Anhaenge } from "../../components/Anhaenge.js";
import type { AnhaengeClient } from "../../components/Anhaenge.js";
import { speichereZahlplan, useZahlplanGriff } from "../../components/vorgangsdetail.js";
import { VorlageDetail, positionsAnzahlText,
         vorlagenVorschauWerte } from "../../components/VorlageDetail.js";
import type { VorlageDetailClient } from "../../components/VorlageDetail.js";
import { formatBetrag } from "../../format.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich ANGEBOTSVORLAGEN — Kopf (Name, Bemerkung, Einleitungs- und
 * Ausleitungstext, Periodenregel) plus die vorbelegten Positionen.
 *
 * **Gestalt seit dem Entscheid vom 06.09.2026.** Die Vorlagenliste steht LINKS im Inhalt
 * (samt ihrem Unternehmensfilter), rechts daneben die gewählte Vorlage; zwischenzeitlich
 * stand sie als zweite Ebene in der Verwaltungs-Spalte. Die Spezifikation nennt die
 * Vorlagen ausdrücklich als Objekt-Bereich.
 *
 * Deshalb ein HOOK und keine Komponente: siehe `VerwaltungBereichTeile` in gemeinsam.tsx.
 *
 * **Gestalt seit Migration 029 (23.09.2026, abends).** Eine Angebotsvorlage IST ein
 * Angebot ohne Kunden; ihre Positionen laufen über denselben Editor wie die eines Angebots
 * (Entscheid Dirk: „Für Vorlagen soll derselbe Editor verwendet werden."). Daraus folgen
 * drei Änderungen an dieser Datei:
 *
 *  - **Der Dialog bearbeitet nur noch EINES: den KOPF** (Name, Bemerkung, beide Texte,
 *    Periodenregel). Die Positionsliste im Dialog — mit eigenen Zeilenknöpfen, eigener
 *    Artikel-Übernahme und eigenem Auf/Ab — ist fort; sie war der zweite Editor. Die
 *    beiden Texte pflegt jetzt `PlatzhalterTextfeld` (components/PositionsEditor.tsx), also
 *    dieselbe Maske wie am Angebot, samt Markdown-Vorschau; die zweite Textmaske mit
 *    eigener Platzhalter-Legende ist damit ebenfalls entfallen.
 *  - **Die vierte Spalte vom Vormittag desselben Tages entfällt wieder.** Sie war die
 *    Antwort auf „wer nachsehen will, was in einer Vorlage steht, muss sie zum BEARBEITEN
 *    öffnen" — ein Schreibweg für eine Leseabsicht. Mit der Angebotsoptik steht die
 *    Leseliste im DETAIL, wo sie auch am Angebot steht; eine eigene Spalte daneben wäre
 *    dieselbe Auskunft zweimal. `ObjektBereich.zusatz` bleibt als Mechanik für andere
 *    Bereiche erhalten (siehe gemeinsam.tsx).
 *  - **Gerechnet wird nicht mehr hier.** `vorlagenpositionen.ts` (Zeilensumme,
 *    Zwischensummen, Endsumme) ist entfallen: Die Zahlen kommen fertig aus `listItems` —
 *    aus derselben Antwort, die auch der Editor benutzt. Es gibt damit genau EINE
 *    Rechnung, und sie steht auf dem Server.
 *
 * **Was im Detail steht.** Alles: Kopfzeile, Summe netto und Zahl der Positionen, die
 * beiden Rohtexte hinter einem Aufklapper und die Positionen — lesend, und mit „Positionen
 * bearbeiten" derselbe `PositionsEditor` wie am Angebot. Gezeichnet wird das nicht hier,
 * sondern im Baustein `components/VorlageDetail.tsx` (Ruling V1); diese Datei bleibt, was
 * sie ist: die LISTE samt Filter, der Anlege- und Änderungsdialog und die Verdrahtung mit
 * der Adresse.
 *
 * Die Texte werden als ROHTEXT mit ihren `#{...}`-Tokens bearbeitet; hier wird NICHTS
 * aufgelöst (Ruling P3B-ENGINE). Die Legende darunter kommt mit der Liste VOM SERVER —
 * `web` darf `domain` nicht importieren, und eine abgetippte Liste wäre eine zweite
 * Wahrheit. Die VORSCHAU der Texte zeigt an einer Vorlage BEISPIELWERTE
 * (`vorlagenVorschauWerte`): Es gibt keinen Kunden, aus dem `#{kunde.name}` käme, und ein
 * stehengelassenes Token sagte dem Bearbeiter nicht, wie sein Satz aussieht.
 *
 * **CSS:** Liste, Filter und Dialog tragen den Block `vw__` (views/Verwaltung.css); das
 * Detail bringt seinen eigenen mit (`vorl__`, components/VorlageDetail.css). Der
 * Spaltenblock `vws__` gehört allein dem Bereichs-Untermenü.
 */

/** Nur die Wege dieses Bereichs. Der Positionsteil kommt über `VorlageDetailClient`
 *  dazu — dieselbe Reihe von Methoden, die auch der Angebots-Editor braucht. */
export type AngebotsvorlagenClient = Pick<ApiClient,
  "listOfferTemplates" | "createOfferTemplate" | "updateOfferTemplate"
  | "deleteOfferTemplate" | "listTaxRates">
  & VorlageDetailClient
  // Der ZAHLPLAN im Kopf-Modal: lesen UND schreiben. Dieselbe Reihe, die auch die beiden
  // Vorgangsmasken benutzen (`ZahlplanModalClient`, components/Zahlplan.tsx) — ein eigenes
  // `Pick` daneben wäre dieselbe Aussage zweimal.
  & ZahlplanModalClient
  // Der Kasten „Anhänge" (Paket „Textbausteine als Anhang", 24.09.2026) — dieselben fünf
  // Wege wie an Angebot und Auftrag; eine Vorlage benutzt vier davon (kein Ausblenden,
  // das gibt es nur am Auftrag).
  & AnhaengeClient;

/** Die Periodenregel einer Vorlage, deutsch beschriftet — sie reist beim Anwenden mit ans
 *  Angebot (Spec, Abschnitt Datenmodell). */
const PERIODENREGELN = [
  { id: "CALENDAR_YEAR", label: "Kalenderjahr" },
  { id: "CONTRACT_YEAR", label: "Vertragsjahr" },
] as const;

type Periodenregel = (typeof PERIODENREGELN)[number]["id"];

/** Der Dialog trägt jetzt NUR den Kopf — die Positionen laufen über den Editor im Detail. */
type VorlagenDialog = {
  /** null = neue Vorlage. */
  id: string | null;
  /** Nur beim Anlegen bedienbar — eine Vorlage lässt sich nicht umhängen. */
  companyId: string;
  name: string;
  note: string;
  introText: string;
  outroText: string;
  periodBasis: Periodenregel;
};

type VorlagenFeld = "name" | "note" | "introText" | "outroText" | "periodBasis"
  | "companyId";

const VORLAGEN_FELDNAMEN: readonly VorlagenFeld[] =
  ["name", "note", "introText", "outroText", "periodBasis", "companyId"];

/**
 * Dieselbe Reihe OHNE `companyId` — für den ÄNDERN-Fall.
 *
 * Dort steht das Unternehmen nur lesend da (eine Vorlage lässt sich nicht umhängen), es gibt
 * also gar kein Feld, an dem ein Feldfehler daran erscheinen könnte: Er verschwände stumm,
 * und der Bearbeiter sähe ein Panel, das sich nicht speichern lässt und nichts dazu sagt.
 * Nicht zugeordnet wandert er in die Sammelmeldung — sichtbar, wenn auch ohne Feld
 * (Befund Fixrunde 1 zu Aufgabe 7).
 */
const VORLAGEN_FELDNAMEN_AENDERN: readonly VorlagenFeld[] =
  VORLAGEN_FELDNAMEN.filter((f) => f !== "companyId");

/**
 * Was beim Löschen MITGEHT, grammatisch richtig: „Sie hat keine Positionen." / „1 Position
 * geht mit." / „4 Positionen gehen mit."
 *
 * Eigener Satz und nicht `positionsAnzahlText`: Jener beschriftet eine Zeile („ohne
 * Positionen"), dieser steht in einer Frage und braucht deshalb ein Verb — und das Verb
 * richtet sich nach der Zahl (Befund Fixrunde 1 zu Aufgabe 7).
 */
function positionsVerlustSatz(anzahl: number): string {
  if (anzahl <= 0) return "Sie hat keine Positionen.";
  return anzahl === 1 ? "1 Position geht mit." : `${anzahl} Positionen gehen mit.`;
}

function vorlagendialogGleich(a: VorlagenDialog, b: VorlagenDialog): boolean {
  return a.id === b.id && a.companyId === b.companyId && a.name === b.name
    && a.note === b.note && a.introText === b.introText && a.outroText === b.outroText
    && a.periodBasis === b.periodBasis;
}

/** Ein leeres Feld ist am Server `null` und nicht `""` — „ohne Bemerkung" ist eine Aussage,
 *  eine leere Zeichenkette wäre eine gespeicherte Leerstelle. */
function leerAlsNull(wert: string): string | null {
  return wert.trim() === "" ? null : wert;
}

/** Der Kopf einer bestehenden Vorlage als Dialog-Entwurf. */
function dialogAus(v: Angebotsvorlage): VorlagenDialog {
  return {
    id: v.id, companyId: v.companyId, name: v.name, note: v.note ?? "",
    introText: v.introText ?? "", outroText: v.outroText ?? "",
    periodBasis: v.periodBasis,
  };
}

/** Wie bei den Vorreitern der Aufgabe 2 — siehe dort die ausführliche Begründung. */
export type VerwaltungAngebotsvorlagenProps = {
  aktiv: boolean;
  client: AngebotsvorlagenClient;
  companies: Unternehmen[];
  /** Die geöffnete Vorlage aus der Adresse (`/verwaltung/angebotsvorlagen/:id`). */
  vorlageId: string | null;
  onOeffnen: (vorlageId: string | null) => void;
  /** Das Arbeitsunternehmen aus der Kopfleiste (ME-Etappe 1, Aufgabe 7) — Vorfilter der
   *  Liste und Vorgabe der Neuanlage. `null`: Bestandsverhalten („alle", erstes). */
  arbeitsunternehmenId: string | null;
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

export function useVerwaltungAngebotsvorlagen(
  p: VerwaltungAngebotsvorlagenProps,
): VerwaltungBereichTeile {
  const { aktiv, vorlageId } = p;

  const [zeilen, setZeilen] = useState<Angebotsvorlage[] | null>(null);
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  // Der Unternehmensfilter startet auf dem Arbeitsunternehmen; „Alle Unternehmen" ("")
  // bleibt der Umschalter (ME-Etappe 1, Aufgabe 7 — siehe `useVorfilter`).
  const vorfilter = useVorfilter(p.arbeitsunternehmenId, p.companies);
  const companyId = vorfilter.wert;
  const setCompanyId = vorfilter.setze;
  const [dialog, setDialog] = useState<VorlagenDialog | null>(null);
  /** Der Stand, wie er geöffnet wurde — nur daran ist zu erkennen, ob im Dialog etwas
   *  Ungespeichertes steht (Rückfrage beim Abbrechen, Verlassen-Wächter). */
  const [geladen, setGeladen] = useState<VorlagenDialog | null>(null);
  const [kopfFehler, setKopfFehler] = useState<Partial<Record<VorlagenFeld, string>>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Der Entwurf des Zahlplan-Abschnitts im Kopf-Modal — Bauart und Begründung bei
   *  `useZahlplanGriff` (components/vorgangsdetail.ts). Der Kasten speichert NICHT selbst:
   *  Das strikte Panel kennt genau zwei Ausgänge. */
  const zahlplanGriff = useZahlplanGriff();
  const [arbeitet, setArbeitet] = useState(false);
  const [loescht, setLoescht] = useState<string | null>(null);
  const [neuLaden, setNeuLaden] = useState(0);
  /**
   * Welche Vorlage ihren POSITIONS-EDITOR offen hat (`null` = keine).
   *
   * Er gehört dem Hook, nicht dem Detail: So bleibt es bei EINER offenen Bearbeitung je
   * Ansicht, und beim Wechsel der Adresse muss er fallen — ein Editor, der über einer
   * anderen Vorlage stehenbliebe, schriebe in die falsche.
   *
   * Er zählt NICHT als „ungespeichert": Der Editor schreibt Schritt für Schritt selbst, es
   * gibt keinen Entwurf, der verloren gehen könnte. Deshalb löst er KEINE Rückfrage beim
   * Verlassen aus — anders als der Kopf-Dialog. Die LISTE sperrt er trotzdem (siehe
   * `listeGesperrt`): Das ist keine Aussage über verlorene Entwürfe, sondern die Form, die
   * der Bereich Angebote vorgibt.
   */
  const [bearbeitetPositionen, setBearbeitetPositionen] = useState<string | null>(null);

  const listOfferTemplates = p.client.listOfferTemplates;

  /** Die JETZT geöffnete Vorlage, ohne Effekt-Neuverdrahtung erreichbar — für den
   *  Wettlauf-Riegel in `speichern` und `loeschen` (siehe dort). Bei jedem Zeichnen
   *  nachgeführt, wie `routeRef` in router.ts. */
  const vorlageIdRef = useRef(vorlageId);
  vorlageIdRef.current = vorlageId;

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listOfferTemplates(companyId === "" ? {} : { companyId })
      .then((antwort) => {
        if (abgebrochen) return;
        setZeilen(antwort.items);
        setPlatzhalter(antwort.platzhalter);
        // Ein geglücktes Nachladen HEILT den alten Fehler (Lehre der Fixrunde 1 zu
        // Aufgabe 2) — der Hook lebt über der ganzen Anwendung.
        setLadeFehler(null);
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(
            meldungAus(fehler, "Die Angebotsvorlagen konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listOfferTemplates, companyId, neuLaden]);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Etappe-1-Lektion): der halb
   * ausgefüllte Dialog samt seinem Ausgangsstand, die Feldfehler, die Sammelmeldung, der
   * offene Positions-Editor und der Ladefehler. Der FILTER bleibt — er ist die Sicht auf
   * die Liste, keine angefangene Eingabe.
   */
  useEffect(() => {
    if (aktiv) return;
    setDialog(null);
    setGeladen(null);
    setKopfFehler({});
    setMeldung(null);
    setBearbeitetPositionen(null);
    setLadeFehler(null);
    // Der Zahlplan-Entwurf gehört zum Dialog und fällt mit ihm — dieselbe Regel wie in
    // `dialogSetzen`/`abbrechen`. Bliebe er stehen, schriebe der nächste Kopf-Dialog (auch
    // an einer anderen Vorlage) die Raten von vorher mit.
    zahlplanGriff.zuruecksetzen();
  }, [aktiv]);

  /* Der Änderungsdialog gehört DER Vorlage, über der er geöffnet wurde — er fällt mit der
     ADRESSE, nicht mit dem Klick, der ihn sonst allein geschlossen hätte. Der
     ANLEGE-Dialog (`id === null`) bleibt: Er gehört keinem Objekt, sondern dem Bereich,
     und steht bei der Liste. */
  useEffect(() => {
    // Beide getrennt und ohne Nebenwirkung IM Aktualisierer: `geladen` trägt dieselbe
    // Kennung wie `dialog` (dialogSetzen setzt beide aus demselben Entwurf), also fällt es
    // nach derselben Regel. Ein setGeladen INNERHALB von setDialog liefe unter StrictMode
    // doppelt.
    setDialog((d) => (d === null || d.id === null || d.id === vorlageId ? d : null));
    setGeladen((g) => (g === null || g.id === null || g.id === vorlageId ? g : null));
    /*
     * Und der Positions-Editor mit — aber nur, wenn er einer ANDEREN Vorlage gehört. Der
     * Vergleich ist der ganze Punkt: Das Anlegen öffnet die frische Vorlage SOFORT im
     * Bearbeiten (Spec), und dabei bewegt sich die Adresse genau auf sie. Ein
     * bedingungsloses Zurücksetzen räumte den eben erbetenen Editor im selben Atemzug weg.
     */
    setBearbeitetPositionen((offen) =>
      (offen === null || offen === vorlageId ? offen : null));
    // Und die Sammelmeldung mit (Fixrunde 1): Sie gehört der Vorlage, über der sie entstand
    // — ein gescheitertes Speichern oder ein Löschfehler der einen darf nicht als Alarm
    // über der nächsten stehenbleiben.
    setMeldung(null);
  }, [vorlageId]);

  /**
   * Trägt der Dialog Ungespeichertes? Der ZAHLPLAN-Abschnitt zählt mit (wie am Angebot,
   * `ungespeichert` in AngebotDetail.tsx): Er meldet seinen Entwurf erst, sobald ihn jemand
   * angefasst hat — und genau dann gibt es etwas zu verlieren. Ohne ihn liesse sich eine
   * eben eingetragene Rate ohne Rückfrage wegwerfen.
   */
  const dialogGeaendert = dialog !== null
    && ((geladen !== null && !vorlagendialogGleich(dialog, geladen))
      || zahlplanGriff.beruehrt);
  useBeforeUnload(dialogGeaendert);

  /**
   * OB gerade bearbeitet wird — ein offener Dialog dieses Bereichs mit Ungespeichertem.
   * Fiele die bearbeitete Vorlage durch einen Filterwechsel aus der Liste, verschwände ihr
   * Detail samt Dialog aus dem Baum — und der Entwurf wäre weg, ohne dass jemand gefragt
   * wurde. Dieser Zustand ist auch das `gesperrt` des Detailbausteins: Solange der Dialog
   * offen steht, führt kein zweiter Weg an derselben Vorlage vorbei.
   */
  const bearbeitung = aktiv && dialogGeaendert;

  /**
   * OB die LISTE gesperrt ist — beim offenen Kopf-Dialog UND beim offenen Positions-Editor
   * (Entscheid Controller 24.09.2026).
   *
   * Der Editor trägt zwar keinen Entwurf, der verloren gehen könnte (er schreibt Schritt für
   * Schritt selbst), aber das ist nicht der einzige Grund für eine gesperrte Liste: Im
   * Bereich Angebote ist sie es genauso, solange ein Vorgang bearbeitet wird. Wer mitten in
   * den Positionen einer Vorlage steht, soll nicht mit einem Klick daneben in einer anderen
   * landen und sich fragen, wohin die Zeile gegangen ist, die er gerade tippt — und ein
   * Filterwechsel, der die bearbeitete Vorlage aus der Liste nimmt, risse den Editor samt
   * seinem gerade geholten Stand aus dem Baum. Beendet wird die Bearbeitung über „Fertig".
   */
  const listeGesperrt = bearbeitung || bearbeitetPositionen !== null;
  vorfilter.nachfuehren(listeGesperrt);

  /**
   * Der Verlassen-Wächter des ROUTERS (Hausmuster). Bestätigtes Verlassen VERWIRFT den
   * Dialog — sonst sagte die Rückfrage die Unwahrheit (Lehre der Fixrunde 1 zu Aufgabe 2).
   *
   * NUR für den ÄNDERUNGSDIALOG (`dialog.id !== null`), wie im Bereich Unternehmen — und
   * das aus einem Grund, den erst der echte Browser zeigte (E2E-Torlauf 24.09.2026): Der
   * Anlege-Dialog schliesst mit `setDialog(null)` und ruft im SELBEN Zug `onOeffnen` für
   * die frische Vorlage. Der Router fragt vor dem Sprung den Wächter — und der ist zu diesem
   * Zeitpunkt noch angemeldet, weil React den Effekt erst nach dem nächsten Zeichnen abmeldet.
   * Ein Browser, der die Rückfrage verneint (Playwright tut das, ein Anwender aus Schreck
   * auch), liesse die neue Vorlage ungeöffnet stehen: Liste gesperrt, Detail leer. Der
   * Anlege-Dialog braucht den Router-Wächter ohnehin nicht — er hängt an keiner Adresse,
   * und vor dem Schliessen des Fensters schützt `useBeforeUnload` weiterhin.
   */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  const aenderungsDialogOffen = dialog !== null && dialog.id !== null;
  useEffect(() => {
    if (!bearbeitung || !aenderungsDialogOffen || registriereVerlassenWaechter === undefined) {
      return;
    }
    return registriereVerlassenWaechter(() => {
      const weiter = window.confirm(
        "Diese Angebotsvorlage hat ungespeicherte Änderungen. Sie gehen verloren. "
        + "Wirklich verlassen?");
      if (weiter) {
        setDialog(null); setGeladen(null); setKopfFehler({});
        setMeldung(null);
        zahlplanGriff.zuruecksetzen();
      }
      return weiter;
    });
  }, [bearbeitung, aenderungsDialogOffen, registriereVerlassenWaechter]);

  function firmenName(id: string): string {
    return p.companies.find((c) => c.id === id)?.shortCode ?? "fremdes Unternehmen";
  }

  function dialogSetzen(entwurf: VorlagenDialog): void {
    setKopfFehler({});
    setMeldung(null);
    setDialog(entwurf);
    setGeladen(entwurf);
    // Der Zahlplan-Entwurf des VORIGEN Dialogs darf nicht überleben: Er hängt an EINER
    // Vorlage, und ein mitgeschleppter Entwurf schriebe die Raten der einen an die
    // andere (dieselbe Regel wie `zahlplan.zuruecksetzen()` beim Aufschlagen des
    // Angebots-Panels).
    zahlplanGriff.zuruecksetzen();
  }

  function neuOeffnen(): void {
    dialogSetzen({
      id: null,
      // Vorbelegt mit dem gefilterten Unternehmen — wer die Vorlagen einer Firma ansieht,
      // legt fast immer für dieselbe an. Unter „Alle Unternehmen" das Arbeitsunternehmen.
      companyId: companyId !== "" ? companyId
        : vorgabeUnternehmen(p.arbeitsunternehmenId, p.companies),
      name: "", note: "", introText: "", outroText: "", periodBasis: "CALENDAR_YEAR",
    });
  }

  /**
   * Speichern — beim Anlegen EIN Aufruf ohne Positionen (Ruling V9), beim Ändern der
   * Kopf-Feldsatz.
   *
   * Nach dem ANLEGEN geht die Adresse auf den frischen Satz UND sein Positions-Editor steht
   * offen (Spec, Abschnitt Oberfläche): Wer eine Vorlage anlegt, will Positionen eintragen
   * — nicht erst eine Zeile in einer Liste suchen.
   */
  async function speichern(): Promise<void> {
    const d = dialog;
    if (d === null) return;
    setKopfFehler({});
    setMeldung(null);
    const kopf = {
      name: d.name,
      note: leerAlsNull(d.note),
      introText: leerAlsNull(d.introText),
      outroText: leerAlsNull(d.outroText),
      periodBasis: d.periodBasis,
    };
    setArbeitet(true);
    try {
      let angelegtId: string | null = null;
      if (d.id === null) {
        angelegtId = (await p.client.createOfferTemplate(
          { companyId: d.companyId, ...kopf })).id;
      } else {
        const kopfNeu = await p.client.updateOfferTemplate(d.id, kopf);
        // Der ZWEITE Weg mit der Version aus der ANTWORT des ersten: Kopf und Zahlplan
        // teilen sich `offer.version`, und die des Modals ist nach dem PATCH veraltet —
        // mit ihr liefe das PUT in eine 409 (dieselbe Kette wie am Angebot).
        const zahlplanOk = await speichereZahlplan({
          client: p.client,
          bezug: { art: "template", vorlageId: d.id },
          version: kopfNeu.version,
          griff: zahlplanGriff,
          beiFehler: (meldungText) => setMeldung(meldungText),
        });
        if (!zahlplanOk) {
          // Der KOPF ist geschrieben, nur der Zahlplan nicht. Die Liste (und mit ihr das
          // Detail) muss ihn trotzdem neu lesen: Sie trägt sonst den alten Namen UND die
          // alte Version — und mit der alten Version liefe der nächste Positionsschritt in
          // eine 409, obwohl der Bearbeiter nur den Zahlplan nachzubessern hat. Der Dialog
          // BLEIBT offen samt seiner Meldung: Der zweite Weg ist noch zu erledigen.
          setNeuLaden((n) => n + 1);
          return;
        }
      }
      // Neu lesen: Ein geänderter Name verschiebt die Zeile innerhalb der Ordnung, eine
      // neue Vorlage landet irgendwo in der Liste. Beides weiss nur der Server.
      setNeuLaden((n) => n + 1);
      setDialog(null);
      setGeladen(null);
      if (angelegtId !== null) {
        setBearbeitetPositionen(angelegtId);
        p.onOeffnen(angelegtId);
      }
    } catch (fehler) {
      // Der Wettlauf-Riegel: Das Speichern dauert, und die Adresse kann sich in der
      // Zwischenzeit bewegt haben (Zurück-Taste, Klick auf eine andere Vorlage). Die
      // Meldung gehört der Vorlage, für die geklickt wurde — über einer anderen wäre sie
      // ein Alarm ohne Anlass. Gelesen aus einem Ref und nicht aus `p`: `p.vorlageId` ist
      // in dieser Funktion der Stand VOM KLICK, nicht der von jetzt.
      if (d.id !== null && vorlageIdRef.current !== d.id) return;
      if (fehler instanceof ApiError && fehler.status === 422) {
        const { felder, unzuordenbar } = feldFehlerAus(
          fehler, d.id === null ? VORLAGEN_FELDNAMEN : VORLAGEN_FELDNAMEN_AENDERN);
        setKopfFehler(felder);
        if (unzuordenbar || Object.keys(felder).length === 0) setMeldung(fehler.message);
      } else {
        setMeldung(meldungAus(fehler, "Unerwarteter Fehler beim Speichern."));
      }
    } finally {
      setArbeitet(false);
    }
  }

  async function loeschen(v: Angebotsvorlage): Promise<void> {
    // Rückfrage, weil es kein Zurück gibt: Eine Vorlage wird nicht stillgelegt, sondern
    // samt ihren Positionen gelöscht. Die ZAHL steht in der Frage — „4 Positionen gehen
    // mit" ist der Unterschied zwischen einem leeren Rahmen und einer Stunde Arbeit.
    if (!window.confirm(
      `„${v.name}" wirklich löschen? ${positionsVerlustSatz(v.itemCount)} `
      + "Das lässt sich nicht rückgängig machen.")) {
      return;
    }
    setMeldung(null);
    setLoescht(v.id);
    try {
      await p.client.deleteOfferTemplate(v.id);
      setZeilen((alt) => (alt === null ? alt : alt.filter((x) => x.id !== v.id)));
      // Und die Adresse mit: Was gelöscht ist, steht in der Liste nicht mehr.
      p.onOeffnen(null);
    } catch (fehler) {
      // Derselbe Wettlauf-Riegel wie beim Speichern (siehe dort).
      if (vorlageIdRef.current !== v.id) return;
      setMeldung(meldungAus(fehler, "Die Vorlage konnte nicht gelöscht werden."));
    } finally {
      setLoescht(null);
    }
  }

  /** Der eine Weg aus dem Modal neben dem Speichern (Hausregel „striktes Modalpanel",
   *  06.09.2026) — mit Rückfrage, wenn etwas Ungespeichertes darin steht. Escape ruft
   *  denselben Weg (siehe `Modal` in @kundeninfo/ui); ein Klick daneben schliesst nicht. */
  function abbrechen(): void {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (arbeitet) return;
    if (!darfAbbrechen(dialogGeaendert)) return;
    setDialog(null);
    setGeladen(null);
    zahlplanGriff.zuruecksetzen();
  }

  /** Der Dialograhmen — für Anlegen (bei der Liste) und Ändern (im Detail) derselbe. Er
   *  trägt die fünf KOPFFELDER; die Positionen stehen im Detail (Migration 029). */
  function dialogRahmen(d: VorlagenDialog): ReactElement {
    const felder = (teil: Partial<VorlagenDialog>): void =>
      setDialog((alt) => (alt === null ? alt : { ...alt, ...teil }));
    return (
      <Modal breite="breit"
        titel={d.id === null ? "Neue Angebotsvorlage" : "Angebotsvorlage ändern"}
        onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
        {meldung !== null && <Meldung text={meldung} />}
        {d.id === null ? (
          <div className="vw__auswahl">
            <label className="vw__label" htmlFor="vw-v-unternehmen">
              Unternehmen der Vorlage
            </label>
            <select id="vw-v-unternehmen" className="vw__select" value={d.companyId}
              aria-invalid={kopfFehler.companyId !== undefined ? true : undefined}
              onChange={(e) => felder({ companyId: e.target.value })}>
              {p.companies.map((c) => (
                <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
              ))}
            </select>
            {kopfFehler.companyId !== undefined && (
              <span className="vw__feldfehler" role="alert">{kopfFehler.companyId}</span>
            )}
          </div>
        ) : (
          // Beim Ändern nur noch anzeigen: Eine Vorlage lässt sich nicht umhängen (ihre
          // Positionen zeigen auf Artikel dieses Unternehmens), der Server nähme das
          // Feld gar nicht an.
          // Dasselbe Feld, nur lesend — und darum dieselbe Beschriftung. Als
          // `Feld bearbeitbar={false}` ist es kein Auswahlfeld und trägt auch kein
          // `<label>`, sodass „das Unternehmen lässt sich hier nicht wechseln" prüfbar
          // bleibt (kein combobox mit diesem Namen).
          <Feld id="vw-v-unternehmen-fest" label="Unternehmen der Vorlage"
            bearbeitbar={false}
            wert={p.companies.find((c) => c.id === d.companyId)?.name ?? "—"}
            onChange={() => undefined} />
        )}
        <Feld id="vw-v-name" label="Name" bearbeitbar wert={d.name}
          onChange={(w) => felder({ name: w })}
          {...(kopfFehler.name !== undefined ? { fehler: kopfFehler.name } : {})} />
        {/* Die Bemerkung ist INTERN und steht auf keinem Beleg — genau wie die Notiz am
            Angebot, aus der sie beim Anwenden wird. */}
        <Feld id="vw-v-notiz" label="Bemerkung" bearbeitbar wert={d.note}
          onChange={(w) => felder({ note: w })}
          {...(kopfFehler.note !== undefined ? { fehler: kopfFehler.note } : {})} />

        {/* DIESELBE Textmaske wie am Angebot (`PlatzhalterTextfeld`): Rohtext oben,
            Markdown-Vorschau darunter, Platzhalter-Legende als Fussnote. Eine eigene
            Fassung in dieser Ansicht wäre die zweite — und die zweite wäre eines Tages die
            andere. */}
        <PlatzhalterTextfeld id="vw-v-intro" label="Einleitungstext" rows={4}
          wert={d.introText} onChange={(w) => felder({ introText: w })}
          variablen={platzhalter}
          werte={vorlagenVorschauWerte(p.companies, d.companyId)} />
        {kopfFehler.introText !== undefined && (
          <span className="vw__feldfehler" role="alert">{kopfFehler.introText}</span>
        )}
        <PlatzhalterTextfeld id="vw-v-outro" label="Ausleitungstext" rows={4}
          wert={d.outroText} onChange={(w) => felder({ outroText: w })}
          variablen={platzhalter}
          werte={vorlagenVorschauWerte(p.companies, d.companyId)} />
        {kopfFehler.outroText !== undefined && (
          <span className="vw__feldfehler" role="alert">{kopfFehler.outroText}</span>
        )}
        <p className="vw__hinweis">
          Die Vorschau zeigt BEISPIELWERTE („Muster GmbH") — eine Vorlage hat keinen Kunden.
          Beim Anwenden setzt der Beleg die echten Werte ein.
        </p>

        {/* Die Periodenregel ist ein KOPFFELD der Vorlage und reist beim Anwenden mit
            (Spec): Sie sagt, worauf sich „je Periode" am Beleg bezieht. */}
        <div className="vw__auswahl">
          <label className="vw__label" htmlFor="vw-v-periode">Periodenregel</label>
          <select id="vw-v-periode" className="vw__select" value={d.periodBasis}
            aria-invalid={kopfFehler.periodBasis !== undefined ? true : undefined}
            onChange={(e) => felder({ periodBasis: e.target.value as Periodenregel })}>
            {PERIODENREGELN.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
          {kopfFehler.periodBasis !== undefined && (
            <span className="vw__feldfehler" role="alert">{kopfFehler.periodBasis}</span>
          )}
        </div>

        {/* DIE ZAHLUNGSVEREINBARUNG — bearbeitbar, aber sie speichert NICHT selbst: Sie
            meldet ihren Entwurf an `zahlplanGriff`, und gespeichert wird unten im Fuss
            (Hausregel „striktes Modalpanel"; ein zweiter Speichern-Knopf wäre ein zweiter
            Ausgang). Beim ANLEGEN gibt es sie nicht: Vor dem ersten Speichern existiert
            kein Beleg, dessen Zahlplan man setzen könnte — dieselbe Regel wie bei den
            Positionen und bei den Parteien am Angebot. */}
        {d.id !== null && (
          <Zahlplan client={p.client} bezug={{ art: "template", vorlageId: d.id }}
            idPraefix="vw-v" verdeckt={arbeitet} befunde={zahlplanGriff.befunde}
            onEntwurf={zahlplanGriff.melde} />
        )}

        {/* Der Kasten „Anhänge" — unter dem Zahlplan (Spec, Abschnitt Oberfläche), aus
            demselben Grund erst ab bestehender Vorlage: Vor dem ersten Speichern gibt es
            keinen Beleg, dessen Anlagen man pflegen könnte. OHNE `nurLesend`: Eine
            Vorlage ist dauerhaft pflegbar — genau dafür gibt es sie. */}
        {d.id !== null && (
          <Anhaenge client={p.client} bezug={{ art: "template", vorlageId: d.id }}
            idPraefix="vw-v-anhaenge" verdeckt={arbeitet}
            // Die AKTUELLE Vorlagenversion geht HEREIN (Review-Befund zur Fixrunde K10,
            // 24.09.2026): Der Kopf/Zahlplan-Weg dieses Modals dreht sie im selben Dialog
            // weiter, ohne dass der Kasten selbst neu liest — ohne diese Eigenschaft liefe
            // sein NÄCHSTER eigener Schreibweg mit einer veralteten Version in eine 409.
            version={zeilen?.find((z) => z.id === d.id)?.version}
            onVersionChange={(version) => {
              setZeilen((alt) => (alt === null ? alt : alt.map((z) => (z.id === d.id
                ? { ...z, version } : z))));
            }} />
        )}

        <ModalFuss speichernText="Speichern" speichernGesperrt={arbeitet}
          abbrechenGesperrt={arbeitet}
          onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
      </Modal>
    );
  }

  const satz = vorlageId === null
    ? null
    : (zeilen ?? []).find((v) => v.id === vorlageId) ?? null;

  // ---------------------------------------------------------------------------
  // Die Objektliste — links im Inhalt
  // ---------------------------------------------------------------------------
  const liste = (
    <>
      <div className="vw__leiste">
        <button type="button" className="vw__primaer" disabled={listeGesperrt}
          onClick={neuOeffnen}>
          Neue Angebotsvorlage
        </button>
      </div>

      <div className="vw__filter" role="search">
        <div className="vw__auswahl">
          <label className="vw__label" htmlFor="vw-v-firma">Unternehmen</label>
          {/*
            * Auch der FILTER ist bei offener Bearbeitung gesperrt (Fixrunde 1) — nicht nur
            * die Objektzeilen daneben. Ein Filterwechsel liest die Liste neu; fiele die
            * gerade bearbeitete Vorlage dabei heraus, verschwände ihr Detail samt dem
            * Dialog bzw. dem Positions-Editor darin aus dem Baum — und Verlassen-Wächter
            * wie Browser-Warnung hingen an einem Entwurf, den niemand mehr sehen oder
            * speichern kann.
            */}
          <select id="vw-v-firma" className="vw__select" value={companyId}
            disabled={listeGesperrt}
            onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">Alle Unternehmen</option>
            {p.companies.map((c) => (
              <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      {ladeFehler === null && zeilen !== null && zeilen.length === 0 && (
        <p className="vw__hinweis">Für diese Auswahl gibt es noch keine Angebotsvorlage.</p>
      )}

      <Objektliste label="Angebotsvorlagen">
        {(zeilen ?? []).map((v) => (
          <Objektzeile
            key={v.id}
            name={v.name}
            marken={[firmenName(v.companyId)]}
            /* Die Meta-Zeile sagt in EINEM Satz, was in der Vorlage steckt (Spec,
               Abschnitt Oberfläche): wie viele Positionen und was sie zusammen wiegen.
               Beide Zahlen kommen aus der Liste des Servers — nachgezählt würde hier
               nichts (Ruling V2). */
            meta={[positionsAnzahlText(v.itemCount),
                   `${formatBetrag(v.netAmount, "EUR")} netto`].join(" · ")}
            gewaehlt={v.id === vorlageId}
            gesperrt={listeGesperrt}
            ariaLabel={`Angebotsvorlage ${v.name}, ${firmenName(v.companyId)}, anzeigen`}
            onOeffnen={() => p.onOeffnen(v.id)}
          />
        ))}
      </Objektliste>

      {/* Der Anlege-Dialog steht bei seinem Knopf, also bei der Liste. */}
      {dialog !== null && dialog.id === null && dialogRahmen(dialog)}
    </>
  );

  // ---------------------------------------------------------------------------
  // Das Detail — GENAU EINE Vorlage
  // ---------------------------------------------------------------------------
  const detail = ((): ReactElement => {
    if (vorlageId === null) {
      return (
        <p className="vw__hinweis">
          Links eine Angebotsvorlage wählen — Texte und Positionen stehen dann hier.
        </p>
      );
    }
    if (satz === null) {
      // Die dreistufige Auskunft für ein Lesezeichen auf eine Vorlage, die die Liste nicht
      // enthält (Etappe-1-Lektion) — der Unternehmensfilter kann sie auch bloss
      // ausgeblendet haben, deshalb nennt der letzte Satz ihn.
      if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
      if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;
      return (
        <p className="vw__hinweis">
          Diese Angebotsvorlage gibt es nicht (mehr) — oder der Unternehmensfilter über der
          Liste blendet sie aus. Bitte links eine wählen.
        </p>
      );
    }

    const v = satz;
    return (
      /* Eigene Identität je Vorlage (Etappe-1-Lektion `key={objekt.id}`). */
      <div key={v.id} className="vw__eintrag">
        {meldung !== null && dialog === null && <Meldung text={meldung} />}
        <VorlageDetail
          client={p.client}
          vorlage={v}
          companies={p.companies}
          platzhalter={platzhalter}
          bearbeitung={bearbeitetPositionen === v.id}
          gesperrt={bearbeitung || loescht === v.id}
          onBearbeiten={() => setBearbeitetPositionen(v.id)}
          onSchliessen={() => { setBearbeitetPositionen(null); setNeuLaden((n) => n + 1); }}
          onKopfAendern={() => dialogSetzen(dialogAus(v))}
          onLoeschen={() => { void loeschen(v); }}
          onFortschreiben={(version, stand) => {
            // Die Liste trägt Version, Betrag und Anzahl — sie kommen aus derselben
            // Antwort, mit der der Editor gerade gearbeitet hat. Ohne dieses
            // Fortschreiben liefe der nächste Positionsschritt in eine 409, und die
            // Meta-Zeile der Objektzeile stünde auf dem Stand von vorher.
            setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === v.id
              ? { ...x, version, netAmount: stand.netAmount,
                  itemCount: stand.items.length }
              : x))));
          }} />

        {/* Der Änderungsdialog steht bei seinem Knopf, also im Detail — und gehört GENAU
            DER Vorlage, über der er geöffnet wurde (Lehre der Fixrunde 1 zu Aufgabe 2). */}
        {dialog !== null && dialog.id === v.id && dialogRahmen(dialog)}
      </div>
    );
  })();

  /*
   * KEIN `zusatz` mehr: Die vierte Spalte mit den Positionen stand hier vom Vormittag bis
   * zum Abend des 23.09.2026. Sie ist mit Migration 029 entfallen — die Positionen stehen
   * jetzt im Detail, wie beim Angebot (siehe Kopfkommentar). Die MECHANIK
   * `ObjektBereich.zusatz` bleibt für andere Bereiche erhalten; dieser benutzt sie nicht.
   */
  return { liste, detail };
}
